// File: src/pages/UsersList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Button,
} from "reactstrap";

import swal from "sweetalert";

import { FaPlus, FaFilter, FaFileExcel } from "react-icons/fa";

// ---- your import-layer ----
import api from "../import/axiosConfig";
import { DeleteAccount } from "../import/ApiEndPoint/ApiCalling";
import {
  View_User_By_Id2,
  // if you already have these exact constants in your Api file, keep them
  // otherwise the fallback routes below will still work if your backend matches them
  PurchaseProductList_Product,
  View_Receipt,
  view_create_order_history,
} from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
// Load large tab components only when the user opens those tabs.
const CustomerList = React.lazy(() => import("./CustomerList"));
const TransporterList = React.lazy(() => import("./TransporterList"));

/* ---------------- CSS ---------------- */
const CSS_MODAL = `
  .modal-backdrop.show { z-index: 1990 !important; }
  .modal.show { z-index: 2000 !important; }
  .modal, .modal-dialog, .modal-content { pointer-events: auto; }
`;

const CSS_TABS = `
  .ul-shell{
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  .ul-tabsbar{
    width: 100%;
    background: #ffffff;
    border-bottom: 1px solid #eef2f7;
    padding: 10px 12px;
  }

  .ul-tabswrap{
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
  }

  .ul-tabs{
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 0;
    position: relative;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .ul-tab{
    flex: 1 1 0;
    text-align: center;
    border: none;
    background: transparent;
    padding: 10px 12px;
    font-weight: 900;
    font-size: 14px;
    color: #0f172a;
    cursor: pointer;
    position: relative;
    user-select: none;
    transition: color .2s ease, transform .15s ease;
  }

  .ul-tab:hover{ transform: translateY(-1px); }
  .ul-tab.active{ color: #ffffff; }

  .ul-indicator{
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: 14px;
    background: linear-gradient(90deg, #2563eb, #1d4ed8);
    transition: transform .28s cubic-bezier(.2,.8,.2,1);
    box-shadow: 0 10px 18px rgba(37,99,235,0.22);
  }

  @keyframes ulPop {
    0% { transform: scale(.96); }
    100% { transform: scale(1); }
  }
  .ul-tab.active{ animation: ulPop .18s ease; }
`;

const CSS_ACTIONBAR = `
  .ul-actionbar{
    width: 100%;
    padding: 12px;
    background: #ffffff;
  }
  .ul-actionrow{
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: nowrap;
  }

  .ul-title{
    font-weight: 1000;
    font-size: 18px;
    letter-spacing: .02em;
    text-transform: uppercase;
    color: #0f172a;
    padding-left: 6px;
    border-left: 4px solid #fc8019;
    line-height: 1;
    white-space: nowrap;
  }

  .ul-spacer{ flex: 1 1 auto; }

  .ul-search{
    width: 360px;
    height: 40px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    padding: 0 12px;
    font-weight: 900;
    outline: none;
    box-shadow: 0 8px 16px rgba(0,0,0,0.04);
  }
  .ul-search:focus{
    border-color: #93c5fd;
    box-shadow: 0 0 0 4px rgba(59,130,246,0.18);
  }

  .ul-icon-btn{
    width: 44px;
    height: 40px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: #fff;
    display:grid;
    place-items:center;
    cursor:pointer;
    box-shadow: 0 8px 16px rgba(0,0,0,0.04);
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .ul-icon-btn:hover{
    transform: translateY(-1px);
    box-shadow: 0 12px 22px rgba(0,0,0,0.08);
  }

  .ul-btn-primary{
    height: 40px;
    padding: 0 14px;
    border-radius: 14px;
    font-weight: 1000;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    border: 1px solid #2563eb;
    background: #2563eb;
    color: #fff;
    display:inline-flex;
    align-items:center;
    gap: 8px;
    box-shadow: 0 10px 18px rgba(37,99,235,0.20);
    transition: transform .12s ease, filter .12s ease;
    white-space: nowrap;
  }
  .ul-btn-primary:hover{
    transform: translateY(-1px);
    filter: brightness(0.98);
  }

  .ul-btn-soft{
    height: 40px;
    padding: 0 12px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: #fff;
    font-weight: 1000;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    display:inline-flex;
    align-items:center;
    gap: 8px;
    cursor:pointer;
    box-shadow: 0 8px 16px rgba(0,0,0,0.04);
    transition: transform .12s ease, box-shadow .12s ease;
    white-space: nowrap;
  }
  .ul-btn-soft:hover{
    transform: translateY(-1px);
    box-shadow: 0 12px 22px rgba(0,0,0,0.08);
  }

  .ul-btn-linklike{
    text-decoration: none;
    color: inherit;
  }
`;

const CSS_TABLE = `
  .user-table-wrap {
    width: 100%;
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #fff;
  }
  .user-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    min-width: 0;
  }
  .user-table thead th {
    background: #f4f7f6;
    color: #0f172a;
    font-weight: 1000;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: .03em;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 10px;
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: center;
    white-space: nowrap;
  }
  .user-table tbody td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #0f172a;
    font-size: 14px;
    text-align: center;
  }
  .user-table tbody tr:hover { background: #f9fbff; cursor:pointer; }
`;

const CSS_PICKER = `
  .listbox {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    max-height: 280px;
    overflow: auto;
    background: #fff;
    padding: 8px;
  }
  .pick-item {
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    margin-bottom: 6px;
    background: #fff;
    font-weight: 900;
  }
`;

const injectOnce = () => {
  if (document.getElementById("userslist-inline-css")) return;
  const style = document.createElement("style");
  style.id = "userslist-inline-css";
  style.innerHTML = `
    ${CSS_MODAL}
    ${CSS_TABS}
    ${CSS_ACTIONBAR}
    ${CSS_TABLE}
    ${CSS_PICKER}
  `;
  document.head.appendChild(style);
};

/* ---------------- helpers ---------------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

// Excel supports at most 32,767 characters in one cell.
// Keep a small safety margin and also remove XML-invalid control characters.
const EXCEL_CELL_MAX_LENGTH = 32000;

const safeExcelCellValue = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : "";
  }

  if (typeof value === "boolean") return value;

  let text;

  if (typeof value === "string") {
    text = value;
  } else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }

  text = String(text || "").replace(
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g,
    " ",
  );

  if (text.length <= EXCEL_CELL_MAX_LENGTH) return text;

  return `${text.slice(0, EXCEL_CELL_MAX_LENGTH - 20)}... [TRUNCATED]`;
};

const USER_EXCEL_HEADERS = [
  "firstName",
  "service",
  "OpeningBalance",
  "Type",
  "email",
  "Aadhar_No",
  "Pan_No",
  "State",
  "City",
  "pincode",
  "Father_name",
  "Father_MobileNo",
  "MotherMobileNo",
  "address1",
  "last_job_Salary",
  "pfPercentage",
  "DOB",
  "mobileNumber",
  "DL_Num",
  "Account_Name",
  "Account_No",
  "Ifsc_code",
  "Country",
  "last_job_AppoitmentDate",
  "panNo",
];

const USER_EXCEL_SAMPLE_ROW = {
  firstName: "SAMPLE USER - DELETE BEFORE UPLOAD",
  service: "781001",
  OpeningBalance: "0",
  Type: "debit",
  email: "sample.user@example.com",
  Aadhar_No: "123412341234",
  Pan_No: "ABCDE1234F",
  State: "ASSAM",
  City: "GUWAHATI",
  pincode: "781001",
  Father_name: "SAMPLE FATHER NAME",
  Father_MobileNo: "919876543210",
  MotherMobileNo: "919876543211",
  address1: "SAMPLE ADDRESS - DELETE THIS ROW BEFORE BULK UPLOAD",
  last_job_Salary: "25000",
  pfPercentage: "8",
  DOB: "1995-01-01",
  mobileNumber: "919876543212",
  DL_Num: "AS0120250000001",
  Account_Name: "SAMPLE BANK",
  Account_No: "123456789012",
  Ifsc_code: "SBIN0000001",
  Country: "INDIA",
  last_job_AppoitmentDate: "2026-04-01",
  panNo: "ABCDE1234F",
};

const ACCOUNT_TYPES = [
  "Capital Account",
  "Reserves and Surplus",
  "Current Liabilities",
  "Sundry Creditors",
  "Duties and Taxes",
  "Provisions",
  "Bank OCC Account",
  "Bank OD Account",
  "Secured Loans",
  "Unsecured Loans",
  "Deposits (Liability)",
  "Fixed Assets",
  "Investments",
  "Current Assets",
  "Bank Accounts",
  "Cash-in-Hand",
  "Deposits (Asset)",
  "Loans and Advances (Asset)",
  "Stock-in-Hand",
  "Sundry Debtors",
  "Sales Account",
  "Purchase Account",
  "Direct Incomes",
  "Indirect Incomes",
  "Direct Expenses",
  "Indirect Expenses",
  "Branch/Divisions",
  "Misc. Expenses (Asset)",
];

const ACCOUNT_EXCEL_HEADERS = [
  "FULL NAME",
  "ACCOUNT",
  "TYPE",
  "BALANCE",
  "OPENING FINANCIAL YEAR",
];

const buildAccountExportRows = (data = []) =>
  (Array.isArray(data) ? data : []).map((row) => [
    safeExcelCellValue(row?.firstName || ""),
    safeExcelCellValue(row?.account || row?.Account || ""),
    safeExcelCellValue(row?.Type || ""),
    safeExcelCellValue(
      row?.OpeningBalance === 0 ? 0 : row?.OpeningBalance || "",
    ),
    safeExcelCellValue(row?.openingFinancialYear || ""),
  ]);

const buildAccountListExcelSheet = (data = [], XLSXLib) => {
  const accountRows = buildAccountExportRows(data);
  const accountHeaderRow = ACCOUNT_TYPES.length + 6;

  const matrix = [
    ["INSTRUCTION", "", "", "", ""],
    ["Strictly forbidden to use these account types only.", "", "", "", ""],
    [],
    ["ALLOWED ACCOUNT TYPES", "", "", "", ""],
    ...ACCOUNT_TYPES.map((accountType) => [accountType, "", "", "", ""]),
    [],
    ACCOUNT_EXCEL_HEADERS,
    ...accountRows,
  ];

  const sheet = XLSXLib.utils.aoa_to_sheet(matrix);
  sheet["!cols"] = [
    { wch: 34 },
    { wch: 32 },
    { wch: 14 },
    { wch: 18 },
    { wch: 24 },
  ];
  sheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
  ];
  sheet["!autofilter"] = {
    ref: `A${accountHeaderRow}:E${Math.max(
      accountHeaderRow + accountRows.length,
      accountHeaderRow + 1,
    )}`,
  };

  return sheet;
};

const normalizeExcelDate = (value) => {
  if (!value) return "";

  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return raw;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const serializeServiceForExcel = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const extractReadableValue = (item) => {
    if (item === null || item === undefined || item === "") return "";

    if (typeof item === "string" || typeof item === "number") {
      return String(item).trim();
    }

    if (typeof item === "object") {
      return toStr(
        item?.pincode ??
          item?.pinCode ??
          item?.Pincode ??
          item?.PIN ??
          item?.zipCode ??
          item?.postalCode ??
          item?.city ??
          item?.City ??
          item?.name ??
          item?.label,
      ).trim();
    }

    return String(item).trim();
  };

  let normalized = value;

  // Older records may already store service areas as JSON text.
  if (typeof normalized === "string") {
    const raw = normalized.trim();
    if (!raw) return "";

    if (raw.startsWith("[") || raw.startsWith("{")) {
      try {
        normalized = JSON.parse(raw);
      } catch {
        return safeExcelCellValue(raw);
      }
    } else {
      return safeExcelCellValue(raw);
    }
  }

  const items = Array.isArray(normalized) ? normalized : [normalized];
  const readableValues = items.map(extractReadableValue).filter(Boolean);

  const uniqueValues = [...new Set(readableValues)];
  if (uniqueValues.length) {
    return safeExcelCellValue(uniqueValues.join(", "));
  }

  return safeExcelCellValue(normalized);
};

const buildUserTemplateExportRows = (data = []) =>
  (Array.isArray(data) ? data : []).map((row) => ({
    firstName: toStr(row?.firstName),
    service: serializeServiceForExcel(row?.service ?? row?.serviceArea),
    OpeningBalance: toStr(row?.OpeningBalance),
    Type: toStr(row?.Type),
    email: toStr(row?.email),
    Aadhar_No: toStr(row?.Aadhar_No),
    Pan_No: toStr(row?.Pan_No),
    State: toStr(row?.State ?? row?.state),
    City: toStr(row?.City ?? row?.city),
    pincode: toStr(row?.pincode),
    Father_name: toStr(row?.Father_name),
    Father_MobileNo: toStr(row?.Father_MobileNo),
    MotherMobileNo: toStr(row?.MotherMobileNo),
    address1: toStr(row?.address1),
    last_job_Salary: toStr(row?.last_job_Salary),
    pfPercentage: toStr(row?.pfPercentage),
    DOB: normalizeExcelDate(row?.DOB),
    mobileNumber: toStr(row?.mobileNumber),
    DL_Num: toStr(row?.DL_Num),
    Account_Name: toStr(row?.Account_Name),
    Account_No: toStr(row?.Account_No),
    Ifsc_code: toStr(row?.Ifsc_code),
    Country: toStr(row?.Country ?? row?.country),
    last_job_AppoitmentDate: normalizeExcelDate(row?.last_job_AppoitmentDate),
    panNo: toStr(row?.panNo),
  }));

const TABS = [
  { key: "users", label: "User List" },
  { key: "accounts", label: "Account List" },
  { key: "customers", label: "Customer List" },
  { key: "transporters", label: "Transporter List" },
  { key: "emergency-invoice", label: "Emergency Invoice" },
  { key: "id-page", label: "ID page of DMS" },
];

const PAGE_SIZE = 100;

/* ---------------- CONFIG FOR ALL MODULE ENDPOINTS ---------------- */
/*
  Put your exact list APIs here if needed.
  If your project already exports these constants, you can replace fallback strings.
*/
const MODULE_ENDPOINTS = {
  users: {
    source: "User",
    pathBuilder: (uid, db) =>
      typeof View_User_By_Id2 === "string" && View_User_By_Id2.trim()
        ? `${View_User_By_Id2.replace(/\/+$/, "")}/${uid}/${db}`
        : `/user/view-user-by-id/${uid}/${db}`,
  },

  sales: {
    source: "Sales",
    pathBuilder: (uid, db) =>
      typeof view_create_order_history === "string" &&
      view_create_order_history.trim()
        ? `${view_create_order_history.replace(/\/+$/, "")}/${uid}/${db}`
        : `/order/view-create-order-history/${uid}/${db}`,
  },

  products: {
    source: "Product",
    pathBuilder: (uid, db) =>
      typeof PurchaseProductList_Product === "string" &&
      PurchaseProductList_Product.trim()
        ? `${PurchaseProductList_Product.replace(/\/+$/, "")}/${uid}/${db}`
        : `/product/view-product/${uid}/${db}`,
  },

  receipts: {
    source: "Receipt",
    pathBuilder: (uid, db) =>
      typeof View_Receipt === "string" && View_Receipt.trim()
        ? `${View_Receipt.replace(/\/+$/, "")}/${uid}/${db}`
        : `/receipt/view-receipt/${uid}/${db}`,
  },

  payments: {
    source: "Payment",
    pathBuilder: (uid, db) => `/payment/view-payment/${uid}/${db}`,
  },

  customers: {
    source: "Customer",
    pathBuilder: (uid, db) => `/customer/view-customer/${uid}/${db}`,
  },

  transporters: {
    source: "Transporter",
    pathBuilder: (uid, db) => `/transporter/view-transporter/${uid}/${db}`,
  },

  accounts: {
    source: "Account",
    pathBuilder: (uid, db) => `/account/view-account/${uid}/${db}`,
  },
};

/* ---------------- UNIVERSAL ID NORMALIZER ---------------- */
class DMSIdCollector {
  static getName(row = {}, source = "") {
    if (source === "Sales") {
      return (
        row?.partyName ||
        row?.PartyName ||
        row?.customerName ||
        row?.CustomerName ||
        row?.name ||
        row?.invoiceParty ||
        row?.billTo ||
        ""
      );
    }

    if (source === "Product") {
      return (
        row?.productName ||
        row?.Product_Name ||
        row?.name ||
        row?.itemName ||
        row?.title ||
        ""
      );
    }

    if (source === "Receipt" || source === "Payment") {
      return (
        row?.particular ||
        row?.Particular ||
        row?.partyName ||
        row?.customerName ||
        row?.userName ||
        row?.expenseName ||
        row?.transporterName ||
        row?.name ||
        ""
      );
    }

    return (
      row?.name ||
      row?.fullName ||
      row?.full_name ||
      row?.firstName ||
      row?.userName ||
      row?.username ||
      row?.partyName ||
      row?.CompanyName ||
      row?.companyName ||
      row?.Account_Name ||
      row?.account ||
      row?.Account ||
      row?.productName ||
      row?.Product_Name ||
      ""
    );
  }

  static getSectionName(row = {}, source = "") {
    if (source === "User") {
      return (
        row?.sectionName ||
        row?.section ||
        row?.departmentName ||
        row?.department ||
        row?.rolename?.departmentName ||
        row?.rolename?.department ||
        "User"
      );
    }

    if (source === "Sales") return "Sales";
    if (source === "Product") return "Product";
    if (source === "Receipt") return "Receipt";
    if (source === "Payment") return "Payment";
    if (source === "Customer") return "Customer";
    if (source === "Transporter") return "Transporter";
    if (source === "Account") return "Account";

    return row?.sectionName || row?.section || source || "";
  }

  static getSubSectionName(row = {}, source = "") {
    if (source === "User") {
      return (
        row?.subSectionName ||
        row?.subSection ||
        row?.roleName ||
        row?.rolename?.roleName ||
        row?.designation ||
        row?.position ||
        ""
      );
    }

    if (source === "Sales") {
      return (
        row?.invoiceType ||
        row?.orderType ||
        row?.salesType ||
        row?.status ||
        "Sales Entry"
      );
    }

    if (source === "Product") {
      return (
        row?.category ||
        row?.catageory ||
        row?.subcategory ||
        row?.subCategory ||
        row?.productType ||
        "Product Entry"
      );
    }

    if (source === "Receipt") {
      return row?.paymentMode || row?.voucherType || "Receipt Entry";
    }

    if (source === "Payment") {
      return row?.paymentMode || row?.voucherType || "Payment Entry";
    }

    if (source === "Customer") {
      return row?.customerType || row?.category || "Customer Entry";
    }

    if (source === "Transporter") {
      return row?.vehicleType || row?.category || "Transporter Entry";
    }

    if (source === "Account") {
      return row?.Type || row?.accountType || "Account Entry";
    }

    return row?.subSectionName || row?.subSection || "";
  }

  static getIdValue(row = {}, source = "") {
    if (source === "Sales") {
      return (
        row?.invoiceNo ||
        row?.invoiceNumber ||
        row?.billNo ||
        row?.orderNo ||
        row?.voucherNo ||
        row?.id ||
        row?._id ||
        ""
      );
    }

    if (source === "Product") {
      return (
        row?.productCode ||
        row?.itemCode ||
        row?.sku ||
        row?.hsn ||
        row?.id ||
        row?._id ||
        ""
      );
    }

    if (source === "Receipt" || source === "Payment") {
      return (
        row?.voucherNo ||
        row?.receiptNo ||
        row?.paymentNo ||
        row?.transactionId ||
        row?.id ||
        row?._id ||
        ""
      );
    }

    return (
      row?.dmsId ||
      row?.dmsID ||
      row?.idCardId ||
      row?.employeeId ||
      row?.userId ||
      row?.referenceId ||
      row?.id ||
      row?._id ||
      ""
    );
  }

  static normalizeArray(items = [], source = "") {
    return (Array.isArray(items) ? items : [])
      .map((row, index) => ({
        __rowKey: `${source}-${row?._id || row?.id || index}`,
        originalRow: row,
        source,
        serialNo: index + 1,
        name: this.getName(row, source),
        sectionName: this.getSectionName(row, source),
        subSectionName: this.getSubSectionName(row, source),
        idValue: this.getIdValue(row, source),
      }))
      .filter(
        (item) =>
          toStr(item.name).trim() ||
          toStr(item.sectionName).trim() ||
          toStr(item.subSectionName).trim() ||
          toStr(item.idValue).trim(),
      );
  }

  static extractListFromResponse(data, source = "") {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.adminDetails) && source === "User")
      return data.adminDetails;
    if (Array.isArray(data?.order)) return data.order;
    if (Array.isArray(data?.orders)) return data.orders;
    if (Array.isArray(data?.products)) return data.products;
    if (Array.isArray(data?.receipts)) return data.receipts;
    if (Array.isArray(data?.payments)) return data.payments;
    if (Array.isArray(data?.customers)) return data.customers;
    if (Array.isArray(data?.transporters)) return data.transporters;
    if (Array.isArray(data?.accounts)) return data.accounts;
    return [];
  }
}

export default function UsersList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("users");

  const [userSearch, setUserSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [idPageSearch, setIdPageSearch] = useState("");

  const [insiderPermissions, setInsiderPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
    BulkUpload: true,
  });

  const [rows, setRows] = useState([]);
  const [idMasterRows, setIdMasterRows] = useState([]);
  const [idLoading, setIdLoading] = useState(false);
  const [idErrorMsg, setIdErrorMsg] = useState("");
  const [idLoaded, setIdLoaded] = useState(false);

  // Keep the full user response for the ID master so we do not call the user API twice.
  const userListForIdRef = useRef([]);
  const idLoadStartedRef = useRef(false);
  const componentMountedRef = useRef(true);

  const [userPage, setUserPage] = useState(1);
  const [accountPage, setAccountPage] = useState(1);
  const [idPagePage, setIdPagePage] = useState(1);

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  /* ---------------- Columns ---------------- */
  const baseColumns = useMemo(
    () => [
      { key: "id", header: "ID", get: (r) => r?.id },
      { key: "firstName", header: "FULL NAME", get: (r) => r?.firstName },
      { key: "email", header: "EMAIL", get: (r) => r?.email },
      {
        key: "mobileNumber",
        header: "MOBILE NUMBER",
        get: (r) => r?.mobileNumber,
      },
      {
        key: "account",
        header: "ACCOUNT",
        get: (r) => r?.account || r?.Account,
      },
      {
        key: "OpeningBalance",
        header: "OPENING BAL.",
        get: (r) => r?.OpeningBalance,
      },
      { key: "Type", header: "BAL TYPE", get: (r) => r?.Type },
      { key: "pincode", header: "PIN CODE", get: (r) => r?.pincode },
      { key: "City", header: "CITY", get: (r) => r?.City || r?.city },
      { key: "State", header: "STATE", get: (r) => r?.State || r?.state },
      { key: "Aadhar_No", header: "AADHAR NO", get: (r) => r?.Aadhar_No },
      { key: "Pan_No", header: "PAN NO", get: (r) => r?.Pan_No },
      { key: "DL_Num", header: "DL NO.", get: (r) => r?.DL_Num },
      { key: "Account_Name", header: "BANK NAME", get: (r) => r?.Account_Name },
      { key: "Account_No", header: "ACCOUNT NO", get: (r) => r?.Account_No },
      { key: "Ifsc_code", header: "IFSC CODE", get: (r) => r?.Ifsc_code },
      {
        key: "DOB",
        header: "DATE OF BIRTH",
        get: (r) => String(r?.DOB || "").split("T")[0],
      },
      { key: "Father_name", header: "FATHER NAME", get: (r) => r?.Father_name },
      { key: "PassportNo", header: "PASSPORT NO", get: (r) => r?.PassportNo },
      { key: "address1", header: "ADDRESS", get: (r) => r?.address1 },
      {
        key: "rolename.roleName",
        header: "ROLE NAME",
        get: (r) => r?.rolename?.roleName,
      },
    ],
    [],
  );

  const accountColumns = useMemo(
    () => [
      {
        key: "id",
        header: "ID",
        get: (r) => r?.id || r?._id || "",
      },
      {
        key: "firstName",
        header: "FULL NAME",
        get: (r) => r?.firstName || "",
      },
      {
        key: "account",
        header: "ACCOUNT",
        get: (r) => r?.account || r?.Account || "",
      },
      {
        key: "Type",
        header: "TYPE",
        get: (r) => r?.Type || "",
      },
      {
        key: "OpeningBalance",
        header: "BALANCE",
        get: (r) => (r?.OpeningBalance === 0 ? 0 : r?.OpeningBalance || ""),
      },
      {
        key: "openingFinancialYear",
        header: "OPENING FINANCIAL YEAR",
        get: (r) => r?.openingFinancialYear || "",
      },
    ],
    [],
  );

  const idPageColumns = useMemo(
    () => [
      { key: "serialNo", header: "SR NO", get: (r) => r?.serialNo },
      { key: "source", header: "MODULE", get: (r) => r?.source },
      { key: "name", header: "NAME", get: (r) => r?.name },
      {
        key: "sectionName",
        header: "SECTION NAME",
        get: (r) => r?.sectionName,
      },
      {
        key: "subSectionName",
        header: "SUB SECTION",
        get: (r) => r?.subSectionName,
      },
      { key: "idValue", header: "ID", get: (r) => r?.idValue },
    ],
    [],
  );

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [baseColumns]);

  const accountColMap = useMemo(() => {
    const map = new Map();
    accountColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [accountColumns]);

  const [userSelectedKeys, setUserSelectedKeys] = useState([]);
  const [accountSelectedKeys, setAccountSelectedKeys] = useState([]);

  useEffect(() => {
    const raw =
      localStorage.getItem("UserListColumns") ||
      localStorage.getItem("AccountSearch");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "string") {
          nextKeys = parsed.filter((k) => colMap.has(k));
        } else if (typeof parsed[0] === "object") {
          for (const item of parsed) {
            const byField =
              item.field && baseColumns.find((c) => c.key === item.field);
            const byHeader =
              item.headerName &&
              baseColumns.find((c) => c.header === item.headerName);
            if (byField) nextKeys.push(byField.key);
            else if (byHeader) nextKeys.push(byHeader.key);
          }
        }
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setUserSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  useEffect(() => {
    const raw = localStorage.getItem("AccountListColumns");
    let nextKeys = [];

    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((key) => accountColMap.has(key));
      }
    } catch {}

    if (!nextKeys.length) {
      nextKeys = accountColumns.map((column) => column.key);
    }

    setAccountSelectedKeys(nextKeys);
  }, [accountColumns, accountColMap]);

  const currentSelectedKeys =
    activeTab === "accounts" ? accountSelectedKeys : userSelectedKeys;
  const setCurrentSelectedKeys =
    activeTab === "accounts" ? setAccountSelectedKeys : setUserSelectedKeys;

  const currentAvailableColumns =
    activeTab === "accounts" ? accountColumns : baseColumns;

  const currentColumnMap = activeTab === "accounts" ? accountColMap : colMap;

  const userVisibleCols = useMemo(
    () => userSelectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [userSelectedKeys, colMap],
  );

  const accountVisibleCols = useMemo(
    () =>
      accountSelectedKeys.map((key) => accountColMap.get(key)).filter(Boolean),
    [accountSelectedKeys, accountColMap],
  );

  /* ---------------- MAIN FETCH ----------------
     Fast path: load only the user/account data required by the default tabs.
     The expensive cross-module DMS ID data is loaded lazily only when that tab opens.
  */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
        const uid = userinfo?._id || userinfo?.id || "";
        const db = userinfo?.database || userinfo?.db || "";
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          "";

        if (!uid || !db) throw new Error("Missing _id or database in userData");

        const perms = CheckPermission("Create User");
        if (perms && typeof perms === "object" && !cancelled) {
          setInsiderPermissions((prev) => ({ ...prev, ...perms }));
        }

        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const userPath = MODULE_ENDPOINTS.users.pathBuilder(uid, db);
        const userRes = await api.get(userPath, { headers });

        if (cancelled) return;

        const userList = Array.isArray(userRes?.data?.adminDetails)
          ? userRes.data.adminDetails
          : DMSIdCollector.extractListFromResponse(userRes?.data, "User");

        userListForIdRef.current = userList;

        const activeOnly = userList.filter((x) => x?.status === "Active");
        setRows(activeOnly.length ? activeOnly : userList);
      } catch (e) {
        if (cancelled) return;

        console.error("[UsersList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load data. Check console for details.",
        );
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------- LAZY DMS ID FETCH ----------------
     This used to run 8 module requests during the first page load.
     Now it runs only when the ID Page of DMS tab is opened, and only once.
  */
  useEffect(() => {
    if (activeTab !== "id-page" || idLoaded || idLoadStartedRef.current) {
      return undefined;
    }

    idLoadStartedRef.current = true;

    (async () => {
      setIdLoading(true);
      setIdErrorMsg("");

      try {
        const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
        const uid = userinfo?._id || userinfo?.id || "";
        const db = userinfo?.database || userinfo?.db || "";
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          "";

        if (!uid || !db) throw new Error("Missing _id or database in userData");

        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const combined = DMSIdCollector.normalizeArray(
          userListForIdRef.current,
          "User",
        );

        // Users are already loaded above, so do not request them again.
        const moduleEntries = Object.entries(MODULE_ENDPOINTS).filter(
          ([key]) => key !== "users",
        );

        const settled = await Promise.allSettled(
          moduleEntries.map(async ([, mod]) => {
            const url = mod.pathBuilder(uid, db);
            const res = await api.get(url, { headers });
            const list = DMSIdCollector.extractListFromResponse(
              res?.data,
              mod.source,
            );
            return DMSIdCollector.normalizeArray(list, mod.source);
          }),
        );

        if (!componentMountedRef.current) return;

        settled.forEach((item) => {
          if (item.status === "fulfilled" && Array.isArray(item.value)) {
            combined.push(...item.value);
          }
        });

        setIdMasterRows(
          combined.map((row, index) => ({
            ...row,
            serialNo: index + 1,
          })),
        );
        setIdLoaded(true);
      } catch (e) {
        if (!componentMountedRef.current) return;

        console.error("[UsersList] ID master fetch error:", e);
        setIdErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load DMS ID data.",
        );

        // Allow a retry if the user leaves and opens the tab again.
        idLoadStartedRef.current = false;
      } finally {
        if (componentMountedRef.current) setIdLoading(false);
      }
    })();

    return undefined;
  }, [activeTab, idLoaded]);

  /* ---------------- Separate functions for tab data ---------------- */
  const getUserTabRows = (list) => {
    const roleNameOf = (r) =>
      String(r?.rolename?.roleName || r?.position || "").trim();

    return (list || []).filter((r) => {
      const rn = roleNameOf(r);
      const isAccount = !!(r?.account || r?.Account);
      return !isAccount && rn !== "Customer" && rn !== "Transporter";
    });
  };

  const getAccountTabRows = (list) => {
    return (list || []).filter((r) => !!(r?.account || r?.Account));
  };

  const applySearch = (list, term, cols) => {
    const t = String(term || "")
      .trim()
      .toLowerCase();

    if (!t) return list || [];

    return (list || []).filter((r) =>
      (cols || []).some((col) => toStr(col.get(r)).toLowerCase().includes(t)),
    );
  };

  const userRows = useMemo(() => getUserTabRows(rows), [rows]);
  const accountRows = useMemo(() => getAccountTabRows(rows), [rows]);

  const userFiltered = useMemo(
    () => applySearch(userRows, userSearch, userVisibleCols),
    [userRows, userSearch, userVisibleCols],
  );

  const accountFiltered = useMemo(
    () => applySearch(accountRows, accountSearch, accountVisibleCols),
    [accountRows, accountSearch, accountVisibleCols],
  );

  const idPageFiltered = useMemo(
    () => applySearch(idMasterRows, idPageSearch, idPageColumns),
    [idMasterRows, idPageSearch, idPageColumns],
  );

  // Render only a small page of rows at a time. Export/search still use the full filtered data.
  const userPageCount = Math.max(1, Math.ceil(userFiltered.length / PAGE_SIZE));
  const accountPageCount = Math.max(
    1,
    Math.ceil(accountFiltered.length / PAGE_SIZE),
  );
  const idPageCount = Math.max(1, Math.ceil(idPageFiltered.length / PAGE_SIZE));

  const safeUserPage = Math.min(userPage, userPageCount);
  const safeAccountPage = Math.min(accountPage, accountPageCount);
  const safeIdPage = Math.min(idPagePage, idPageCount);

  const pagedUserRows = useMemo(
    () =>
      userFiltered.slice(
        (safeUserPage - 1) * PAGE_SIZE,
        safeUserPage * PAGE_SIZE,
      ),
    [userFiltered, safeUserPage],
  );

  const pagedAccountRows = useMemo(
    () =>
      accountFiltered.slice(
        (safeAccountPage - 1) * PAGE_SIZE,
        safeAccountPage * PAGE_SIZE,
      ),
    [accountFiltered, safeAccountPage],
  );

  const pagedIdRows = useMemo(
    () =>
      idPageFiltered.slice(
        (safeIdPage - 1) * PAGE_SIZE,
        safeIdPage * PAGE_SIZE,
      ),
    [idPageFiltered, safeIdPage],
  );

  useEffect(() => setUserPage(1), [userSearch, userSelectedKeys]);
  useEffect(() => setAccountPage(1), [accountSearch, accountSelectedKeys]);
  useEffect(() => setIdPagePage(1), [idPageSearch]);

  /* ---------------- Column chooser modal logic ---------------- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };

  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...currentSelectedKeys, ...addBufferRef.current]),
    );
    setCurrentSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = currentSelectedKeys.slice();
    next.splice(selectedIndex, 1);
    setCurrentSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = currentSelectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setCurrentSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= currentSelectedKeys.length - 1)
      return;
    const next = currentSelectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setCurrentSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    if (activeTab === "accounts") {
      localStorage.setItem(
        "AccountListColumns",
        JSON.stringify(accountSelectedKeys),
      );
    } else {
      localStorage.setItem("UserListColumns", JSON.stringify(userSelectedKeys));
    }
    closeModal();
  };

  /* ---------------- Exports ---------------- */
  const getActiveExportPayload = () => {
    if (activeTab === "accounts") {
      return {
        cols: accountColumns,
        data: accountFiltered,
        name: "AccountList",
      };
    }

    if (activeTab === "id-page") {
      return {
        cols: idPageColumns,
        data: idPageFiltered,
        name: "DMS_ID_Master",
      };
    }

    return {
      cols: userVisibleCols,
      data: userFiltered,
      name: "UserList",
    };
  };

  const buildExportRows = (cols, data) => {
    return (data || []).map((r) => {
      const obj = {};
      (cols || []).forEach((c) => {
        obj[c.header] = safeExcelCellValue(c.get(r));
      });
      return obj;
    });
  };

  const handleExportPDF = async () => {
    const { cols, data, name } = getActiveExportPayload();

    const header = cols.map((c) => c.header).join(",");
    const lines = (data || []).map((r) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    const { exportDataToPDF } = await import("../import/Downloader");
    await exportDataToPDF(csv, name);
  };

  const handleExportXLSX = async () => {
    try {
      // XLSX and file-saver are large dependencies; load them only when Export is clicked.
      const XLSXModule = await import("xlsx");
      const XLSX = XLSXModule.default || XLSXModule;
      const fileSaverModule = await import("file-saver");
      const saveAs = fileSaverModule.saveAs || fileSaverModule.default;

      let exportRows;
      let name;
      let ws;
      let sheetName = "Sheet1";

      if (activeTab === "users") {
        exportRows = buildUserTemplateExportRows(userFiltered);

        // Show a sample only when the complete user list has no users.
        // A search with no matches still exports only the template headers.
        if (!userRows.length) {
          exportRows = [{ ...USER_EXCEL_SAMPLE_ROW }];
        }

        name = "UserList";
        sheetName = "User Bulk Upload";

        const matrix = [
          USER_EXCEL_HEADERS,
          ...exportRows.map((row) =>
            USER_EXCEL_HEADERS.map((header) =>
              safeExcelCellValue(row?.[header] ?? ""),
            ),
          ),
        ];

        ws = XLSX.utils.aoa_to_sheet(matrix);
        ws["!cols"] = USER_EXCEL_HEADERS.map((header) => ({
          wch: ["address1", "service"].includes(header) ? 34 : 18,
        }));
        ws["!autofilter"] = {
          ref: `A1:Y${Math.max(matrix.length, 2)}`,
        };
      } else if (activeTab === "accounts") {
        name = "AccountList";
        sheetName = "Account List";
        ws = buildAccountListExcelSheet(accountFiltered, XLSX);
      } else {
        const payload = getActiveExportPayload();
        exportRows = buildExportRows(payload.cols, payload.data);
        name = payload.name;
        ws = XLSX.utils.json_to_sheet(exportRows);
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${name}.xlsx`,
      );
    } catch (error) {
      console.error("[UsersList] Excel export failed:", error);
      swal(
        "Excel Export Failed",
        error?.message || "Could not create the Excel file.",
        "error",
      );
    }
  };

  /* ---------------- Create / Row click handlers ---------------- */
  const handleCreate = () => {
    if (activeTab === "accounts") {
      navigate("/app/accounts/create/0");
      return;
    }
    navigate("/app/users/create/0");
  };

  const handleRowClick = (r) => {
    const id = r?._id || r?.id;
    if (!id && activeTab !== "id-page") return;

    if (activeTab === "accounts") {
      navigate(`/app/accounts/create/${id}`);
      return;
    }

    if (activeTab === "id-page") {
      const source = r?.source;
      const raw = r?.originalRow || {};
      const rid = raw?._id || raw?.id;
      if (!rid) return;

      if (source === "User") navigate(`/app/users/create/${rid}`);
      else if (source === "Account") navigate(`/app/accounts/create/${rid}`);
      else if (source === "Customer") navigate(`/app/customers/create/${rid}`);
      else if (source === "Transporter")
        navigate(`/app/transporter/create/${rid}`);
      else if (source === "Product") navigate(`/app/products/create/${rid}`);
      else if (source === "Receipt") navigate(`/app/receipt/create/${rid}`);
      else if (source === "Payment") navigate(`/app/payment/create/${rid}`);
      else if (source === "Sales") navigate(`/app/order/edit/${rid}`);
      return;
    }

    navigate(`/app/users/create/${id}`);
  };

  /* ---------------- Delete (kept) ---------------- */
  const handleDelete = (id) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        DeleteAccount(id)
          .then(() => setRows((prev) => prev.filter((x) => x._id !== id)))
          .catch(() => {});
      }
    });
  };

  /* ---------------- Render functions ---------------- */
  const renderUserTab = () => {
    const noData = !userFiltered.length;
    return (
      <>
        {renderActionBar({
          title: "User List",
          searchValue: userSearch,
          onSearchChange: setUserSearch,
          createLabel: "+ USER",
        })}

        <Card
          style={{ padding: 8, borderRadius: 14, border: "1px solid #e5e7eb" }}
        >
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              <Spinner size="sm" color="primary" /> Loading users...
            </div>
          ) : noData ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              No users found.
            </div>
          ) : (
            <div className="user-table-wrap">
              <table className="user-table">
                <thead>
                  <tr>
                    {userVisibleCols.map((c) => (
                      <th key={c.key}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedUserRows.map((r) => (
                    <tr key={r?._id || r?.id} onClick={() => handleRowClick(r)}>
                      {userVisibleCols.map((c) => (
                        <td key={c.key}>{toStr(c.get(r))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading &&
            renderPagination(
              userFiltered.length,
              safeUserPage,
              setUserPage,
              userPageCount,
            )}
        </Card>
      </>
    );
  };

  const renderAccountTab = () => {
    const noData = !accountFiltered.length;
    return (
      <>
        {renderActionBar({
          title: "Account List",
          searchValue: accountSearch,
          onSearchChange: setAccountSearch,
          createLabel: "+ ACCOUNT",
        })}

        <Card
          style={{ padding: 8, borderRadius: 14, border: "1px solid #e5e7eb" }}
        >
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              <Spinner size="sm" color="primary" /> Loading accounts...
            </div>
          ) : noData ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              No accounts found.
            </div>
          ) : (
            <div className="user-table-wrap">
              <table className="user-table">
                <thead>
                  <tr>
                    {accountVisibleCols.map((c) => (
                      <th key={c.key}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedAccountRows.map((r) => (
                    <tr key={r?._id || r?.id} onClick={() => handleRowClick(r)}>
                      {accountVisibleCols.map((c) => (
                        <td key={c.key}>{toStr(c.get(r))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading &&
            renderPagination(
              accountFiltered.length,
              safeAccountPage,
              setAccountPage,
              accountPageCount,
            )}
        </Card>
      </>
    );
  };

  const renderCustomerTab = () => {
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, textAlign: "center" }}>
            Loading customers...
          </div>
        }
      >
        <CustomerList />
      </React.Suspense>
    );
  };

  const renderTransporterTab = () => {
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, textAlign: "center" }}>
            Loading transporters...
          </div>
        }
      >
        <TransporterList />
      </React.Suspense>
    );
  };

  const renderEmergencyInvoiceTab = () => {
    return (
      <Card
        style={{ padding: 24, borderRadius: 14, border: "1px solid #e5e7eb" }}
      >
        <div style={{ textAlign: "center", color: "#6b7280" }}>
          Emergency Invoice page not connected yet.
        </div>
      </Card>
    );
  };

  const renderIdPageTab = () => {
    const noData = !idPageFiltered.length;

    return (
      <>
        {renderActionBar({
          title: "ID Page of DMS",
          searchValue: idPageSearch,
          onSearchChange: setIdPageSearch,
          createLabel: "",
        })}

        <Card
          style={{ padding: 8, borderRadius: 14, border: "1px solid #e5e7eb" }}
        >
          {idLoading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              Loading DMS ID records...
            </div>
          ) : idErrorMsg ? (
            <Alert color="danger" style={{ margin: 8 }}>
              {idErrorMsg}
            </Alert>
          ) : noData ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
              No ID records found.
            </div>
          ) : (
            <div className="user-table-wrap">
              <table className="user-table">
                <thead>
                  <tr>
                    {idPageColumns.map((c) => (
                      <th key={c.key}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedIdRows.map((r) => (
                    <tr key={r.__rowKey} onClick={() => handleRowClick(r)}>
                      {idPageColumns.map((c) => (
                        <td key={c.key}>{toStr(c.get(r))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!idLoading &&
            !idErrorMsg &&
            renderPagination(
              idPageFiltered.length,
              safeIdPage,
              setIdPagePage,
              idPageCount,
            )}
        </Card>
      </>
    );
  };

  /* ---------------- Tab bar indicator calculations ---------------- */
  const activeIndex = useMemo(
    () =>
      Math.max(
        0,
        TABS.findIndex((t) => t.key === activeTab),
      ),
    [activeTab],
  );
  const tabWidthPct = 100 / TABS.length;

  /* The shell renders immediately; loading is shown inside the active table. */

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <div className="ul-shell">
            <div className="ul-tabsbar">
              <div className="ul-tabswrap">
                <div className="ul-tabs">
                  <div
                    className="ul-indicator"
                    style={{
                      width: `${tabWidthPct}%`,
                      transform: `translateX(${activeIndex * 100}%)`,
                    }}
                  />
                  {TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`ul-tab ${activeTab === t.key ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab(t.key);
                        setModal(false);
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ padding: 12 }}>
              {activeTab === "users" && renderUserTab()}
              {activeTab === "accounts" && renderAccountTab()}
              {activeTab === "customers" && renderCustomerTab()}
              {activeTab === "transporters" && renderTransporterTab()}
              {activeTab === "emergency-invoice" && renderEmergencyInvoiceTab()}
              {activeTab === "id-page" && renderIdPageTab()}
            </div>
          </div>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="listbox">
                {currentAvailableColumns.map((col) => (
                  <div key={col.key} className="pick-item">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="listbox" style={{ flex: 1 }}>
                  {currentSelectedKeys.map((key, i) => {
                    const col = currentColumnMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );

  function renderPagination(total, page, setPage, pageCount) {
    if (total <= PAGE_SIZE) return null;

    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: "12px 8px 4px",
        }}
      >
        <Button
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
        >
          Previous
        </Button>
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          {start}-{end} of {total} · Page {page} of {pageCount}
        </span>
        <Button
          size="sm"
          disabled={page >= pageCount}
          onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
        >
          Next
        </Button>
      </div>
    );
  }

  /* ---------------- Action bar renderer ---------------- */
  function renderActionBar({
    title,
    searchValue,
    onSearchChange,
    createLabel,
  }) {
    const showActions =
      activeTab === "users" ||
      activeTab === "accounts" ||
      activeTab === "id-page";

    if (!showActions) return null;

    return (
      <div className="ul-actionbar">
        <div className="ul-actionrow">
          <div className="ul-title">{title}</div>

          <div className="ul-spacer" />

          <input
            className="ul-search"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />

          {(activeTab === "users" || activeTab === "accounts") && (
            <div
              className="ul-icon-btn"
              title="Filter Columns"
              onClick={openModal}
            >
              <FaFilter size={14} />
            </div>
          )}

          {insiderPermissions?.Download && (
            <>
              {/* <button
                type="button"
                className="ul-btn-soft"
                title="Download PDF"
                onClick={handleExportPDF}
              >
                <FaFilePdf size={16} />
                PDF
              </button> */}

              <button
                type="button"
                className="ul-btn-soft"
                title="Download Excel"
                onClick={handleExportXLSX}
              >
                <FaFileExcel size={16} />
                EXCEL
              </button>

              {/* {activeTab !== "id-page" && insiderPermissions?.BulkUpload && (
                <a
                  className="ul-btn-linklike"
                  href={CreateUserSample}
                  download
                  title="Download Format"
                >
                  <span className="ul-btn-soft">
                    <ImDownload size={16} />
                    FORMAT
                  </span>
                </a>
              )} */}
            </>
          )}

          {(activeTab === "users" || activeTab === "accounts") &&
            insiderPermissions?.Create && (
              <button
                type="button"
                className="ul-btn-primary"
                onClick={handleCreate}
              >
                <FaPlus size={12} />
                {createLabel}
              </button>
            )}
        </div>
      </div>
    );
  }
}
